import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "../lib/utils"
import { Button } from "./Button"

interface PaginationProps {
  page: number
  pageCount: number
  onPageChange: (page: number) => void
  /** Total rows + page size for the "x–y of z" summary. */
  total?: number
  pageSize?: number
  onPageSizeChange?: (pageSize: number) => void
  pageSizeOptions?: number[]
  itemLabel?: string
  className?: string
}

function getPages(page: number, pageCount: number): (number | "…")[] {
  if (pageCount <= 7)
    return Array.from({ length: pageCount }, (_, i) => i + 1)
  const pages: (number | "…")[] = [1]
  const start = Math.max(2, page - 1)
  const end = Math.min(pageCount - 1, page + 1)
  if (start > 2) pages.push("…")
  for (let i = start; i <= end; i++) pages.push(i)
  if (end < pageCount - 1) pages.push("…")
  pages.push(pageCount)
  return pages
}

export function Pagination({
  page,
  pageCount,
  onPageChange,
  total,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  itemLabel = "Items",
  className,
}: PaginationProps) {
  if (pageCount <= 1 && total === 0) return null
  const pages = getPages(page, Math.max(pageCount, 1))

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center justify-between gap-3 text-xs sm:flex-row select-none",
        className
      )}
    >
      {/* Left: Items / Assets per page */}
      <div className="flex items-center gap-2 text-muted-foreground">
        {pageSize != null && onPageSizeChange && (
          <>
            <span>{itemLabel} per page:</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-8 rounded-md border border-border bg-card px-2 text-xs font-medium text-foreground outline-none transition-colors hover:border-primary focus:border-primary cursor-pointer"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </>
        )}
      </div>

      {/* Right: Showing summary + Prev/Next buttons */}
      <div className="flex flex-wrap items-center gap-3">
        {total != null && pageSize != null && (
          <p className="text-xs text-muted-foreground whitespace-nowrap">
            Showing{" "}
            <span className="font-semibold text-foreground">
              {total === 0 ? 0 : (page - 1) * pageSize + 1}
            </span>
            –
            <span className="font-semibold text-foreground">
              {Math.min(page * pageSize, total)}
            </span>{" "}
            of <span className="font-semibold text-foreground">{total}</span>
          </p>
        )}

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            aria-label="Previous page"
            className="size-7 rounded-md p-0"
          >
            <ChevronLeft className="size-3.5" />
          </Button>
          {pages.map((p, i) =>
            p === "…" ? (
              <span
                key={`gap-${i}`}
                className="px-1 text-xs text-muted-foreground"
              >
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p)}
                className={cn(
                  "size-7 rounded-md text-xs font-medium transition-colors cursor-pointer flex items-center justify-center tabular-nums",
                  p === page
                    ? "bg-primary text-primary-foreground font-semibold shadow-xs"
                    : "border border-border bg-card text-foreground hover:bg-secondary"
                )}
              >
                {p}
              </button>
            )
          )}
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pageCount}
            aria-label="Next page"
            className="size-7 rounded-md p-0"
          >
            <ChevronRight className="size-3.5" />
          </Button>
        </div>
      </div>
    </div>
  )
}
